import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { asPlainText } from "../lib/richtext";

const SITE = "https://mrtrophee.ca";

export const KEYWORDS_FR = "trophées personnalisés, trophée Québec, médailles personnalisées, plaques honorifiques, coupes et trophées, trophées sportifs, trophée en cristal, trophée en verre, trophée acrylique, trophée résine, trophée perpétuel, coupe de championnat, médaille sportive personnalisée, médailles avec rubans, médailles sur mesure, plaque de reconnaissance, plaque commémorative, plaque de porte gravée, badge nominatif personnalisé, plaquette d'identité, cadeaux gravés, cadeaux d'entreprise personnalisés, bijoux gravés, montre gravée personnalisée, bague gravée, bouteille de vin gravée, verres gravés, gourde personnalisée, tasse personnalisée, objets promotionnels personnalisés, vêtements corporatifs, enseignes commerciales, signalétique intérieure, affichage grand format, bannières publicitaires, gravure laser, gravure personnalisée, gravure laser Québec, gravure CNC, gravure sur bois, gravure sur verre, gravure sur cuir, gravure sur métal, gravure sur acrylique, gravure de bague, gravure photo, impression UV, sublimation, sérigraphie, lettrage commercial, infographie et logos, meilleur prix garanti, trophée pas cher, médailles pas chères, récompenses abordables, prix de reconnaissance, récompenses d'entreprise, personnalisation de trophées, boutique de trophées en ligne, acheter trophées en ligne, soumission gratuite, livraison rapide partout au Canada, production express, rabais de quantité, trophées tournoi hockey, trophées tournoi soccer, trophées tournoi de golf, prix pour gala, prix de participation enfants, trophée fin d'année scolaire, cadeau de retraite gravé, cadeaux de mariage gravés, trophées ligue sportive, récompenses scolaires, prix employé du mois, reconnaissance années de service, trophées Montérégie, trophées Montréal, trophées rive-sud de Montréal, ramassage Léry, atelier de gravure Québec, gravure Châteauguay, trophées Canada, fait au Québec";
export const KEYWORDS_EN = "custom trophies, trophy Quebec, personalized medals, honour plaques, cups and trophies, sports trophies, crystal trophy, glass awards, acrylic awards, resin trophies, perpetual trophies, championship cup, personalized sports medals, medals with ribbons, custom medals, recognition plaque, commemorative plaque, engraved door sign, custom name badges, engraved gifts, personalized corporate gifts, engraved jewelry, engraved watch, engraved ring, engraved wine bottle, engraved glasses, personalized water bottle, custom tumbler, promotional products, corporate apparel, commercial signage, large format printing, laser engraving, custom engraving, laser engraving Quebec, CNC engraving, wood engraving, glass engraving, leather engraving, metal engraving, photo engraving, UV printing, sublimation, screen printing, best price guaranteed, cheap trophies, affordable awards, corporate awards, trophy customization, online trophy shop, buy trophies online, free quote, fast delivery across Canada, rush production, quantity discounts, hockey tournament trophies, soccer tournament trophies, golf tournament awards, gala awards, participation awards for kids, employee of the month award, years of service awards, engraved retirement gift, engraved wedding gifts, trophies Monteregie, trophies Montreal, trophies South Shore Montreal, pickup Lery, engraving shop Quebec, trophies Canada, made in Quebec";

// Mots-clés personnalisés dans l'admin (Paramètres → Mots-clés SEO) — chargés une fois
let kwPromise = null;
const loadSeoKeywords = () => {
  if (!kwPromise) {
    kwPromise = api.get("/settings/shop")
      .then((r) => ({ fr: (r.data?.seo_keywords_fr || "").trim(), en: (r.data?.seo_keywords_en || "").trim() }))
      .catch(() => ({ fr: "", en: "" }));
  }
  return kwPromise;
};

// Fusionne les mots-clés admin avec ceux par défaut, sans doublon (casse ignorée) :
// les moteurs et les IA reçoivent TOUJOURS la liste complète, jamais une liste tronquée.
export const mergeKeywords = (...lists) => {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    for (const k of (list || "").split(",")) {
      const kw = k.trim();
      if (kw && !seen.has(kw.toLowerCase())) { seen.add(kw.toLowerCase()); out.push(kw); }
    }
  }
  return out.join(", ");
};

// Fiche d'entreprise (schema.org) lue par Google, Bing et les robots des IA :
// identité, adresse, téléphone, zone desservie — présente sur TOUTES les pages.
const businessLd = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "Store"],
  "@id": "https://mrtrophee.ca/#entreprise",
  name: "Mr Trophée",
  url: SITE,
  image: `${SITE}/partage-sociaux.jpg`,
  telephone: "+1-514-565-6665",
  email: "info@trophee.ca",
  priceRange: "$$",
  currenciesAccepted: "CAD",
  paymentAccepted: "Credit Card, PayPal",
  address: {
    "@type": "PostalAddress",
    streetAddress: "1314 ch. du Lac-Saint-Louis",
    addressLocality: "Léry",
    addressRegion: "QC",
    postalCode: "J6N 1B1",
    addressCountry: "CA",
  },
  geo: { "@type": "GeoCoordinates", latitude: 45.3407383, longitude: -73.8113982 },
  areaServed: { "@type": "Country", name: "Canada" },
  contactPoint: { "@type": "ContactPoint", telephone: "+1-514-565-6665", email: "info@trophee.ca", contactType: "customer service", availableLanguage: ["French", "English"] },
  sameAs: ["https://m.me/trophee.ca"],
};

// Note Google publique (5,0 ★ · N avis) — lue UNE fois (promise partagée) et exposée
// en schema.org aggregateRating sur TOUTES les pages via businessLd (demande du
// propriétaire, 2026-10-06 : étoiles dans les résultats enrichis + réponses des IA)
let ratingPromise = null;
const loadRating = () => {
  if (!ratingPromise) {
    ratingPromise = api.get("/testimonials", { params: { lang: "fr" } })
      .then((r) => (r.data?.source === "google" && r.data?.rating
        ? { rating: r.data.rating, count: r.data.user_rating_count || 0 }
        : null))
      .catch(() => null);
  }
  return ratingPromise;
};

export function SEO({ title, description, path = "/", image = "/partage-sociaux.jpg", noindex = false, jsonLd = null, keywords = null }) {  const { lang } = useLang();
  const [custom, setCustom] = useState(null);
  const [rating, setRating] = useState(null);  // note Google publique → aggregateRating
  useEffect(() => { loadSeoKeywords().then(setCustom); }, []);
  useEffect(() => { loadRating().then(setRating); }, []);
  // Rapport Bing (titres/descriptions en double) : les balises SEO statiques du
  // index.html (utiles aux robots SANS JavaScript, ex. PerplexityBot) portent
  // data-static-seo="1" — Helmet (enfant) a déjà posé les siennes quand cet effet
  // tourne ; on retire les statiques pour ne JAMAIS avoir de doublon dans le DOM.
  useEffect(() => {
    document.head.querySelectorAll("[data-static-seo]").forEach((el) => el.remove());
  }, []);
  // URLs FR / EN dédiées (demande du propriétaire, 2026-10-06) : chaque page a sa
  // jumelle anglaise sous /en/… — canonical = la page courante, hreflang croisés.
  // NOTE : la vraie adresse est lue dans window.location (le Routes parent retire
  // le préfixe /en pour le routage — useLocation() renverrait le chemin SANS /en)
  const realPath = window.location.pathname;
  const isEnPath = realPath === "/en" || realPath.startsWith("/en/");
  const url = `${SITE}${path}`;
  const enUrl = `${SITE}${path === "/" ? "/en" : `/en${path}`}`;
  const selfUrl = isEnPath ? enUrl : url;
  // Un champ SEO formaté dans l'admin (gras/couleur) redevient du texte brut ici
  const safeTitle = asPlainText(title);
  const safeDesc = asPlainText(description);
  const img = image?.startsWith("http") ? image : `${SITE}${image}`;
  const customKw = custom ? (lang === "fr" ? custom.fr : custom.en) : "";
  // Ordre de fusion (sans doublon) : mots-clés propres à la page (ex. catégorie)
  // EN TÊTE, puis ceux de l'admin, puis la liste de base du site
  const kw = mergeKeywords(keywords, customKw, lang === "fr" ? KEYWORDS_FR : KEYWORDS_EN);
  // Données structurées : les mots-clés sont aussi inscrits dans le JSON-LD
  // (lu par Google, Bing et les robots des IA), sans toucher au contenu existant.
  // La fiche LocalBusiness (adresse, téléphone, zone) est jointe sur chaque page,
  // ENRICHIE de la note Google réelle (aggregateRating — étoiles et réponses d'IA) ;
  // sans jsonLd fourni par la page, un schéma WebSite par défaut porte les mots-clés.
  const biz = {
    ...businessLd,
    keywords: kw,
    ...(rating?.count ? {
      aggregateRating: {
        "@type": "AggregateRating", ratingValue: rating.rating,
        reviewCount: rating.count, bestRating: 5, worstRating: 1,
      },
    } : {}),
  };
  const ld = jsonLd
    ? [biz, ...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((e) =>
        e && typeof e === "object" && !e.keywords ? { ...e, keywords: kw } : e)]
    : [biz,
       { "@context": "https://schema.org", "@type": "WebSite", name: "Mr Trophée", url: SITE, keywords: kw }];
  return (
    <Helmet>
      <html lang={lang} />
      <title>{safeTitle}</title>
      <meta name="description" content={safeDesc} />
      <meta name="keywords" content={kw} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      <meta name="geo.region" content="CA-QC" />
      <meta name="geo.placename" content="Léry" />
      <meta name="geo.position" content="45.3407383;-73.8113982" />
      <meta name="ICBM" content="45.3407383, -73.8113982" />
      <link rel="canonical" href={selfUrl} />
      <link rel="alternate" hrefLang="fr" href={url} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Mr Trophée" />
      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={safeDesc} />
      <meta property="og:url" content={selfUrl} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content={lang === "fr" ? "fr_CA" : "en_CA"} />
      <meta property="og:locale:alternate" content={lang === "fr" ? "en_CA" : "fr_CA"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={safeDesc} />
      <meta name="twitter:image" content={img} />
      {ld && (
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      )}
    </Helmet>
  );
}
